import { useCallback } from 'react'
import {
  GetContextMenuItems,
  GetContextMenuItemsParams,
  MenuItemDef,
} from 'ag-grid-community'
import { intl } from '../../../../i18n'
import {
  ContextMenuItemId,
  getMenuIconHtml,
} from '../../../containers/commons/AgGrid/lib/contextMenu'
import { getSelectedNode } from '../../../containers/BulkSheetView/lib/gridApi'
import { getChildren } from '../../../containers/BulkSheetView/lib/tree'
import { ProgramManagementRow } from '../programManagement'
import { programItemRowService } from '../programItemRowService'

export const useProgramManagementContextMenu = ({
  addGroupRowAction,
  addChildGroupRowAction,
  addProjectRowAction,
  deleteRowAction,
  allRows,
}: {
  addGroupRowAction: () => void
  addChildGroupRowAction: (parentRow: ProgramManagementRow) => void
  addProjectRowAction: (parentRow: ProgramManagementRow) => void
  deleteRowAction: () => void
  allRows: ProgramManagementRow[]
}): {
  contextMenu: GetContextMenuItems<ProgramManagementRow>
} => {
  const contextMenu = useCallback(
    (
      params: GetContextMenuItemsParams<ProgramManagementRow>
    ): MenuItemDef<ProgramManagementRow>[] => {
      const selectedNodes = getSelectedNode(params.api)
      const items: MenuItemDef<ProgramManagementRow>[] = []

      const singleSelected =
        selectedNodes.length === 1 ? selectedNodes[0] : undefined
      const singleSelectedData = singleSelected?.data

      // グループ追加: 常に表示
      items.push({
        name: intl.formatMessage({
          id: 'programManagement.contextMenu.addGroup',
        }),
        icon: getMenuIconHtml(ContextMenuItemId.ADD_ROW),
        action: addGroupRowAction,
      })

      // 子グループ追加: GROUP行を1行選択中のみ有効
      const canAddChildGroup =
        singleSelectedData &&
        programItemRowService.isGroupRow(singleSelectedData)
      items.push({
        name: intl.formatMessage({
          id: 'programManagement.contextMenu.addChildGroup',
        }),
        icon: getMenuIconHtml(ContextMenuItemId.ADD_CHILD_ROW),
        disabled: !canAddChildGroup,
        action: () => {
          if (singleSelectedData) {
            addChildGroupRowAction(singleSelectedData)
          }
        },
      })

      // プロジェクト追加: GROUP行またはPROGRAM行を1行選択中のみ有効
      const canAddProject =
        singleSelectedData &&
        (programItemRowService.isGroupRow(singleSelectedData) ||
          programItemRowService.isProgramRow(singleSelectedData))
      items.push({
        name: intl.formatMessage({
          id: 'programManagement.contextMenu.addProject',
        }),
        icon: getMenuIconHtml(ContextMenuItemId.ADD_CHILD_ROW),
        disabled: !canAddProject,
        action: () => {
          if (singleSelectedData) {
            addProjectRowAction(singleSelectedData)
          }
        },
      })

      // 行削除: PROJECT行またはGROUP行選択中のみ有効
      const allDeletable = selectedNodes.every(
        node =>
          node.data &&
          (programItemRowService.isProjectRow(node.data) ||
            programItemRowService.isGroupRow(node.data))
      )
      const hasChildrenGroup = selectedNodes.some(
        node =>
          node.data &&
          programItemRowService.isGroupRow(node.data) &&
          getChildren(allRows, node.data).length > 0
      )
      items.push({
        name: intl.formatMessage(
          { id: 'bulksheet.contextMenu.delete' },
          { count: selectedNodes.length }
        ),
        icon: getMenuIconHtml(ContextMenuItemId.REMOVE_ROW),
        disabled:
          selectedNodes.length === 0 || !allDeletable || hasChildrenGroup,
        action: deleteRowAction,
      })

      return items
    },
    [
      addGroupRowAction,
      addChildGroupRowAction,
      addProjectRowAction,
      deleteRowAction,
      allRows,
    ]
  )

  return { contextMenu }
}
